import React, { useEffect, useRef, useState } from "react";

import type { ClientId } from "../../domain/types";
import { Callout } from "../../components/Callout";
import { CoButton } from "../../components/Button";
import { CheckIcon, CircleIcon, WarningIcon, XIcon } from "../../components/icons";
import { cn } from "../../components/cn";
import { tallyLedgerApi, type TallyNoBillState } from "../../data/tallyLedgerApi";

const PREFIX = "No Bill - ";

function fold(value: string): string {
  return value.trim().toLowerCase();
}

function labelOf(name: string): string {
  return name.startsWith(PREFIX) ? name.slice(PREFIX.length).trim() : name;
}

export function TallyNoBillCreatePanel({
  clientId,
  companyName,
  onChanged,
}: {
  clientId: ClientId;
  companyName: string;
  onChanged?: () => void;
}) {
  const [state, setState] = useState<TallyNoBillState | null>(null);
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const liveRef = useRef(true);

  useEffect(() => {
    liveRef.current = true;
    tallyLedgerApi
      .noBill(clientId)
      .then((next) => {
        if (!liveRef.current) return;
        setState(next);
        setPicked(new Set((next.rows ?? []).filter((row) => !row.is_parent).map((row) => row.label)));
      })
      .catch((cause: unknown) => {
        if (liveRef.current) {
          setError(cause instanceof Error ? cause.message : "Could not read the No Bill ledgers.");
        }
      });
    return () => {
      liveRef.current = false;
    };
  }, [clientId]);

  useEffect(() => {
    if (state?.import?.status !== "committing") return;
    const timer = window.setInterval(() => {
      tallyLedgerApi
        .noBill(clientId)
        .then((next) => {
          if (!liveRef.current) return;
          setState(next);
          if (next.import?.status === "done") onChanged?.();
        })
        .catch(() => undefined);
    }, 2000);
    return () => window.clearInterval(timer);
  }, [clientId, state?.import?.status, onChanged]);

  if (state === null) {
    return (
      <div className="flex flex-col gap-3 rounded-md border p-4">
        <span className="text-xs font-medium text-muted-foreground">No Bill ledgers</span>
        {error !== null ? <Callout variant="warning">{error}</Callout> : <p className="text-sm text-muted-foreground">Loading…</p>}
      </div>
    );
  }

  const existingLabels = new Set(state.existing.map((row) => fold(labelOf(row.name))));
  const staged = state.import;
  const pushed = staged?.status === "done";
  const rows = state.rows ?? [];
  const stagedLabels = new Set(rows.filter((row) => !row.is_parent).map((row) => fold(row.label)));
  const added = Array.from(picked).filter((label) => !existingLabels.has(fold(label)));
  const wantedNew = added.map(fold);
  const stale =
    staged?.status === "validated" &&
    (wantedNew.length !== stagedLabels.size || wantedNew.some((label) => !stagedLabels.has(label)));
  const toCreate = staged?.new_rows ?? 0;
  const previewing = staged !== null && staged.status === "validated" && !stale && toCreate > 0;
  const books = `${companyName}'s Tally`;
  const stagedByLabel = new Map(rows.filter((row) => !row.is_parent).map((row) => [fold(row.label), row]));
  const parentStatus: "in-books" | "created" | "to-add" =
    rows.find((row) => row.is_parent)?.status === "created" ? "created" : state.parent.exists ? "in-books" : "to-add";

  function addTyped() {
    const label = typed.trim().replace(/^No Bill -\s*/i, "");
    if (label === "") return;
    setPicked((previous) => new Set(previous).add(label));
    setTyped("");
  }

  function remove(label: string) {
    setPicked((previous) => {
      const next = new Set(previous);
      next.delete(label);
      return next;
    });
  }

  function stage() {
    setBusy(true);
    setError(null);
    tallyLedgerApi
      .stageNoBill(clientId, added)
      .then((next) => {
        if (!liveRef.current) return;
        setState(next);
        setPicked(new Set((next.rows ?? []).filter((row) => !row.is_parent).map((row) => row.label)));
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not work out what would be created.");
      })
      .finally(() => setBusy(false));
  }

  function create() {
    setCreating(true);
    setError(null);
    tallyLedgerApi
      .commitNoBill(clientId)
      .then((next) => {
        if (liveRef.current) setState(next);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not create those ledgers.");
      })
      .finally(() => {
        if (liveRef.current) setCreating(false);
      });
  }

  const tree = (
    <ul className="flex flex-col gap-1 text-sm">
      <TreeRow label={state.parent.name} status={parentStatus} depth={0} />
      {state.existing.map((child) => (
        <TreeRow
          key={child.guid ?? child.name}
          label={child.name}
          status={stagedByLabel.get(fold(labelOf(child.name)))?.status === "created" ? "created" : "in-books"}
          depth={1}
        />
      ))}
      {added.map((label) => {
        const row = stagedByLabel.get(fold(label));
        const refused = row?.status === "failed";
        return (
          <TreeRow
            key={label}
            label={`${PREFIX}${label}`}
            status={row?.status === "created" ? "created" : refused ? "refused" : "to-add"}
            error={refused ? row?.error : null}
            depth={1}
            onRemove={!previewing && !busy && !creating && row?.status !== "created" ? () => remove(label) : undefined}
          />
        );
      })}
    </ul>
  );

  return (
    <div className="flex flex-col gap-4 rounded-md border p-4">
      <span className="text-xs font-medium text-muted-foreground">No Bill ledgers</span>
      {error !== null && <Callout variant="warning">{error}</Callout>}

      {previewing ? (
        <div className="flex flex-col gap-3 rounded-md border border-amber-500/40 bg-amber-500/5 p-4">
          <p className="text-sm">
            This adds {toCreate} {toCreate === 1 ? "ledger" : "ledgers"} to <span className="font-medium">{books}</span>.
          </p>
          {tree}
        </div>
      ) : (
        <>
          {tree}
          {!pushed && (
            <div className="flex flex-col gap-2">
              <span className="text-xs font-medium text-muted-foreground">Sub-ledgers (optional)</span>
              <div className="flex items-center gap-2">
                <input
                  value={typed}
                  onChange={(event) => setTyped(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      addTyped();
                    }
                    if (event.key === "Escape") setTyped("");
                  }}
                  placeholder="Travel, staff welfare, festival expenses…"
                  disabled={busy || creating}
                  className="h-9 flex-1 rounded-md border bg-background px-3 text-sm"
                />
                <CoButton variant="outline" onClick={addTyped} disabled={typed.trim() === "" || busy || creating}>
                  Add
                </CoButton>
              </div>
            </div>
          )}
        </>
      )}

      {state.import?.status === "committing" && <p className="text-sm text-muted-foreground">Creating them in Tally…</p>}

      <div className="flex flex-wrap items-center gap-3 border-t pt-3">
        {creating || state.import?.status === "committing" ? (
          <CoButton disabled>Creating…</CoButton>
        ) : pushed ? (
          <CoButton
            onClick={() => {
              setBusy(true);
              tallyLedgerApi
                .closeNoBill(clientId)
                .then((next) => {
                  if (!liveRef.current) return;
                  setState(next);
                  setPicked(new Set());
                })
                .finally(() => setBusy(false));
            }}
            disabled={busy}
          >
            Close
          </CoButton>
        ) : staged === null || stale || staged.status !== "validated" ? (
          <>
            <CoButton
              onClick={stage}
              disabled={busy || creating || (state.parent.exists && added.length === 0)}
            >
              {busy ? "Working…" : stale ? "Work it out again" : "Review what would be created"}
            </CoButton>
            {staged?.status === "validated" && (
              <CoButton
                variant="outline"
                disabled={busy || creating}
                onClick={() => {
                  setBusy(true);
                  tallyLedgerApi
                    .cancelNoBill(clientId)
                    .then((next) => {
                      if (!liveRef.current) return;
                      setState(next);
                      setPicked(new Set());
                    })
                    .finally(() => setBusy(false));
                }}
              >
                Start over
              </CoButton>
            )}
          </>
        ) : (
          <>
            <CoButton onClick={create} disabled={creating || busy || toCreate === 0}>
              {creating ? "Creating…" : "Create ledgers"}
            </CoButton>
            <CoButton
              variant="outline"
              onClick={() => {
                setBusy(true);
                tallyLedgerApi
                  .cancelNoBill(clientId)
                  .then((next) => {
                    if (!liveRef.current) return;
                    setState(next);
                    setPicked(new Set());
                  })
                  .finally(() => setBusy(false));
              }}
              disabled={busy || creating}
            >
              Start over
            </CoButton>
          </>
        )}
        {stale && <span className="text-xs text-muted-foreground">The list changed. Work it out again.</span>}
      </div>
    </div>
  );
}

export function TallyNoBillMappingPanel({ clientId, clientName }: { clientId: ClientId; clientName: string }) {
  const [state, setState] = useState<TallyNoBillState | null>(null);
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [filter, setFilter] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    tallyLedgerApi
      .noBill(clientId)
      .then((next) => {
        if (!live) return;
        setState(next);
        setPicked(new Set(next.mapped.map((row) => row.guid)));
      })
      .catch((cause: unknown) => {
        if (live) setError(cause instanceof Error ? cause.message : "Could not read the ledgers.");
      });
    return () => {
      live = false;
    };
  }, [clientId]);

  if (state === null) {
    return (
      <div className="flex flex-col gap-3 rounded-md border p-4">
        <span className="text-xs font-medium text-muted-foreground">Their No Bill ledgers</span>
        {error !== null ? <Callout variant="warning">{error}</Callout> : <p className="text-sm text-muted-foreground">Loading…</p>}
      </div>
    );
  }

  const options = state.candidates.concat(
    state.mapped
      .filter((row) => !state.candidates.some((candidate) => candidate.guid === row.guid))
      .map((row) => ({ name: row.name, guid: row.guid })),
  );
  const query = filter.trim().toLowerCase();
  const shown = options.filter(
    (candidate) => query === "" || candidate.name.toLowerCase().includes(query) || (candidate.guid != null && picked.has(candidate.guid)),
  );
  const stored = new Set(state.mapped.map((row) => row.guid));
  const unsaved = picked.size !== stored.size || Array.from(picked).some((id) => !stored.has(id));

  return (
    <div className="flex flex-col gap-4 rounded-md border p-4">
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">Their No Bill ledgers</span>
        <p className="text-sm text-muted-foreground">
          Tick the ledgers they already use when there is no bill. Nothing in Tally changes.
        </p>
      </div>
      {error !== null && <Callout variant="warning">{error}</Callout>}
      <div className="flex flex-col gap-2">
        <span className="text-xs font-medium text-muted-foreground">{clientName}&apos;s expense ledgers</span>
        {options.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No expense ledgers were read from their company, so there is nothing to point at yet.
          </p>
        ) : (
          <>
            <input
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              placeholder={`Filter ${options.length} ledgers`}
              disabled={busy}
              className="h-9 rounded-md border bg-background px-3 text-sm"
            />
            {shown.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nothing matches &quot;{filter.trim()}&quot;.</p>
            ) : (
              <ul className="grid max-h-64 gap-1.5 overflow-y-auto pr-1 sm:grid-cols-2">
                {shown.map((candidate) => {
                  const id = candidate.guid;
                  if (!id) return null;
                  const checked = picked.has(id);
                  return (
                    <li key={id}>
                      <label
                        className={cn(
                          "flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm transition-colors",
                          checked ? "border-primary/50 bg-primary/5" : "hover:bg-muted/40",
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={busy}
                          onChange={() => {
                            setPicked((previous) => {
                              const next = new Set(previous);
                              if (next.has(id)) next.delete(id);
                              else next.add(id);
                              return next;
                            });
                          }}
                          className="size-3.5 shrink-0 accent-primary"
                        />
                        <span className="truncate">{candidate.name}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3 border-t pt-3">
        <CoButton
          onClick={() => {
            setBusy(true);
            setError(null);
            tallyLedgerApi
              .saveNoBillMapping(clientId, Array.from(picked))
              .then((next) => {
                setState(next);
                setPicked(new Set(next.mapped.map((row) => row.guid)));
                setSavedAt(new Date().toLocaleTimeString());
              })
              .catch((cause: unknown) => {
                setError(cause instanceof Error ? cause.message : "Could not save the mapping.");
              })
              .finally(() => setBusy(false));
          }}
          disabled={busy || !unsaved}
        >
          {busy ? "Saving…" : picked.size === 0 ? "Save - none of them" : `Save ${picked.size} ${picked.size === 1 ? "ledger" : "ledgers"}`}
        </CoButton>
        <span className="text-xs text-muted-foreground">
          {unsaved
            ? "Not saved yet."
            : savedAt !== null
              ? `Saved at ${savedAt}.`
              : state.mapped.length === 0
                ? "Nothing recorded yet."
                : `${state.mapped.length} ${state.mapped.length === 1 ? "ledger" : "ledgers"} recorded.`}
        </span>
      </div>
      {state.mapped.length > 0 && (
        <ul className="flex flex-wrap gap-1.5 border-t pt-3">
          {state.mapped.map((row) => (
            <li key={row.guid} className="inline-flex items-center gap-1.5 rounded-md border bg-muted/40 px-2 py-1 text-sm">
              <CheckIcon className="size-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
              {row.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TreeRow({
  label,
  error,
  status,
  depth,
  onRemove,
}: {
  label: string;
  error?: string | null;
  status: "in-books" | "created" | "to-add" | "refused";
  depth: number;
  onRemove?: () => void;
}) {
  const caption = status === "created" ? "created" : status === "refused" ? "refused" : null;
  return (
    <li className={cn("flex flex-col gap-0.5", depth > 0 && "ml-2 border-l pl-3")}>
      <span className="flex items-center gap-1.5">
        {status === "in-books" || status === "created" ? (
          <CheckIcon className="size-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
        ) : status === "refused" ? (
          <WarningIcon className="size-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
        ) : (
          <CircleIcon className="size-3.5 shrink-0 text-muted-foreground/40" />
        )}
        <span className={cn("truncate", status === "to-add" && "text-muted-foreground")}>{label}</span>
        {caption !== null && (
          <span className="shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground/60">{caption}</span>
        )}
        {onRemove !== undefined && (
          <button type="button" onClick={onRemove} aria-label={`Remove ${label}`} className="ml-auto shrink-0 text-muted-foreground hover:text-foreground">
            <XIcon className="size-3.5" />
          </button>
        )}
      </span>
      {error ? <span className="pl-5 text-xs text-amber-700 dark:text-amber-400">{error}</span> : null}
    </li>
  );
}
